import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { collection, getDocs, query, doc, setDoc, getDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { PhotoTaskTemplate, PhotoTaskTemplateItem } from '@otw/models/photos'
import { photoTaskTemplateConverter } from '@otw/models/converters/photoTaskTemplateConverter'
import { uploadBytes, getDownloadURL, ref } from '@firebase/storage'
import { storage } from 'config/firebase'

export const usePhotoTaskTemplates = () => {
  const queryClient = useQueryClient()

  const {
    data: templates,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<PhotoTaskTemplate[]>({
    queryKey: ['photoTaskTemplates'],
    queryFn: async () => {
      const ref = collection(db, 'photo_task_templates').withConverter(photoTaskTemplateConverter)
      const q = query(ref)
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })

  // Create template with specific ID
  const createTemplate = useMutation({
    mutationFn: async (template: Omit<PhotoTaskTemplate, 'createdAt' | 'updatedAt'>) => {
      const ref = doc(db, 'photo_task_templates', template.id).withConverter(photoTaskTemplateConverter)
      await setDoc(ref, template)
      return template.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photoTaskTemplates'] })
    },
  })

  // Update template tasks
  const updateTemplateTasks = useMutation({
    mutationFn: async ({ templateId, tasks }: { templateId: string; tasks: Omit<PhotoTaskTemplateItem, 'createdAt' | 'updatedAt'>[] }) => {
      const ref = doc(db, 'photo_task_templates', templateId).withConverter(photoTaskTemplateConverter)
      const snapshot = await getDoc(ref)
      const currentData = snapshot.data()
      if (!currentData) throw new Error('Template not found')

      await setDoc(ref, {
        ...currentData,
        tasks
      }, { merge: true })
      
      return templateId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photoTaskTemplates'] })
    },
  })

  // Upload photo for a task (just uploads to storage, returns URL)
  const uploadPhotoForTask = async (templateId: string, taskId: string, file: File) => {
    const storagePath = `photo_task_examples/${templateId}/${taskId}.jpg`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  // Upload video for a task (just uploads to storage, returns URL)
  const uploadVideoForTask = async (templateId: string, taskId: string, file: File) => {
    const storagePath = `photo_task_examples/${templateId}/${taskId}.mp4`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  // Get template by flow and optionally by company_id
  // Falls back to default template if company-specific template doesn't exist
  const getTemplateByFlow = (flow: 'preDelivery' | 'postDelivery' | 'guest', companyId?: string) => {
      if (companyId) {
      // First try to find company-specific template
      const companyTemplateId = `${flow}-${companyId}`;
      const companyTemplate = (templates || []).find(t => 
        t.id === companyTemplateId && t.company_id === companyId
      );
      
      // If company-specific template exists, return it
      if (companyTemplate) {
        return companyTemplate;
      }
      
      // Otherwise, fall back to default template
      const defaultTemplateId = `${flow}-default`;
      const defaultTemplate = (templates || []).find(t => 
        t.id === defaultTemplateId && !t.company_id
      );
      return defaultTemplate || null;
    } else {
      // No company ID provided, use default template
      const defaultTemplateId = `${flow}-default`;
      const defaultTemplate = (templates || []).find(t => 
        t.id === defaultTemplateId && !t.company_id
      );
      return defaultTemplate || null;
    }
  };

  // Get the default template (for backward compatibility)
  const defaultTemplate = templates?.find(t => t.id === 'default') || templates?.[0] || null

  return {
    templates: templates || [],
    defaultTemplate: defaultTemplate || null,
    isLoading,
    isError,
    error,
    refetch,
    createTemplate,
    updateTemplateTasks,
    uploadPhotoForTask,
    uploadVideoForTask,
    getTemplateByFlow,
  }
}
